import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { buildApiUrl, API_CONFIG } from '../../config/api';
import {
  NdcFactura,
  NdcBuscarResponse,
  NdcResultItem,
} from '../../types';

type WizardStep = 1 | 2 | 3;

const BATCH_SIZE = 20;
const EMIT_TIMEOUT_MS = 120000;

const formatCurrency = (value?: number | null) => {
  if (value == null) return '—';
  return new Intl.NumberFormat('es-CR', {
    style: 'currency',
    currency: 'CRC',
  }).format(value);
};

const NotaCreditoWizard: React.FC = () => {
  const [step, setStep] = useState<WizardStep>(1);
  const [referenciasInput, setReferenciasInput] = useState('');
  const [elegibles, setElegibles] = useState<NdcFactura[]>([]);
  const [noElegibles, setNoElegibles] = useState<NdcFactura[]>([]);
  const [noEncontrados, setNoEncontrados] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [notas, setNotas] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [processed, setProcessed] = useState(0);
  const [totalToProcess, setTotalToProcess] = useState(0);
  const [resultItems, setResultItems] = useState<NdcResultItem[]>([]);
  const [emitFinished, setEmitFinished] = useState(false);
  const emitStartedRef = useRef(false);

  const selectedFacturas = elegibles.filter((f) => selectedIds.has(f.envio_id));

  const handleBuscar = async () => {
    setError('');
    if (!referenciasInput.trim()) {
      setError('Ingrese al menos una referencia externa');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post<NdcBuscarResponse>(
        buildApiUrl(API_CONFIG.CICLA.NDC_BUSCAR),
        { referencias: referenciasInput }
      );
      setElegibles(response.data.elegibles);
      setNoElegibles(response.data.no_elegibles);
      setNoEncontrados(response.data.no_encontrados);
      setSelectedIds(new Set(response.data.elegibles.map((f) => f.envio_id)));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al buscar facturas');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (envioId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(envioId)) next.delete(envioId);
      else next.add(envioId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === elegibles.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(elegibles.map((f) => f.envio_id)));
    }
  };

  const handleVolver = () => {
    if (processing) return;
    setStep((s) => (s > 1 ? ((s - 1) as WizardStep) : s));
  };

  const handleReiniciar = () => {
    if (processing) return;
    setStep(1);
    setReferenciasInput('');
    setElegibles([]);
    setNoElegibles([]);
    setNoEncontrados([]);
    setSelectedIds(new Set());
    setNotas('');
    setError('');
    setResultItems([]);
    setProcessed(0);
    setTotalToProcess(0);
    setEmitFinished(false);
    emitStartedRef.current = false;
  };

  const runEmission = useCallback(async () => {
    const ids = Array.from(selectedIds);
    setTotalToProcess(ids.length);
    setProcessed(0);
    setEmitFinished(false);

    const initialItems: NdcResultItem[] = ids.map((envioId) => {
      const factura = elegibles.find((f) => f.envio_id === envioId);
      return {
        envio_id: envioId,
        referenciaexterna: factura?.referenciaexterna,
        referencia_int: factura?.referencia_int,
        ok: false,
        message: 'Pendiente',
        status: 'pendiente' as const,
      };
    });
    setResultItems(initialItems);

    let processedCount = 0;

    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      const chunk = ids.slice(i, i + BATCH_SIZE);
      try {
        const response = await axios.post(
          buildApiUrl(API_CONFIG.CICLA.NDC_EMITIR_LOTE),
          { envio_ids: chunk, notas },
          { timeout: EMIT_TIMEOUT_MS }
        );

        const resultados = response.data.resultados || [];
        setResultItems((prev) =>
          prev.map((item) => {
            const res = resultados.find(
              (r: { envio_id: number }) => r.envio_id === item.envio_id
            );
            if (!res) return item;
            return {
              ...item,
              ok: res.ok,
              message: res.message,
              referenciaexterna: res.referenciaexterna ?? item.referenciaexterna,
              referencia_int: res.referencia_int ?? item.referencia_int,
              status: res.ok ? 'ok' : 'error',
            };
          })
        );
      } catch (err: any) {
        const msg =
          err.response?.data?.detail || err.message || 'Error al emitir lote';
        setResultItems((prev) =>
          prev.map((item) =>
            chunk.includes(item.envio_id)
              ? { ...item, ok: false, message: msg, status: 'error' }
              : item
          )
        );
      }
      processedCount += chunk.length;
      setProcessed(processedCount);
    }

    setEmitFinished(true);
    setProcessing(false);
  }, [selectedIds, notas, elegibles]);

  useEffect(() => {
    if (step === 3 && !emitStartedRef.current) {
      emitStartedRef.current = true;
      setProcessing(true);
      runEmission();
    }
  }, [step, runEmission]);

  const progressPercent =
    totalToProcess > 0 ? Math.round((processed / totalToProcess) * 100) : 0;

  const okCount = resultItems.filter((r) => r.status === 'ok').length;
  const errorCount = resultItems.filter((r) => r.status === 'error').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Emisión de Notas de Crédito
          </h2>

          <div className="flex items-center mb-6">
            <div
              className={`flex-1 text-center p-2 rounded text-sm ${
                step >= 1
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              1. Buscar y seleccionar
            </div>
            <div className="w-8 border-t mx-2 border-gray-300" />
            <div
              className={`flex-1 text-center p-2 rounded text-sm ${
                step >= 2
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              2. Confirmar
            </div>
            <div className="w-8 border-t mx-2 border-gray-300" />
            <div
              className={`flex-1 text-center p-2 rounded text-sm ${
                step >= 3
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              3. Procesar
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
              {error}
            </div>
          )}

          {step === 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Referencias externas (separadas por coma)
              </label>
              <textarea
                value={referenciasInput}
                onChange={(e) => setReferenciasInput(e.target.value)}
                rows={3}
                placeholder="LEA-2483-32-FE, LEA-2481-32-FE (también puede pegar una por línea)"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
              />
              <div className="mt-4 flex gap-2">
                <button
                  onClick={handleBuscar}
                  disabled={loading}
                  className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {loading ? 'Buscando...' : 'Buscar'}
                </button>
                {elegibles.length > 0 && (
                  <button
                    onClick={() => setStep(2)}
                    disabled={selectedIds.size === 0}
                    className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    Siguiente ({selectedIds.size})
                  </button>
                )}
              </div>

              {(noEncontrados.length > 0 || noElegibles.length > 0) && (
                <div className="mt-4 space-y-2">
                  {noEncontrados.length > 0 && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
                      <strong>No encontradas:</strong>{' '}
                      {noEncontrados.join(', ')}
                    </div>
                  )}
                  {noElegibles.length > 0 && (
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded text-sm">
                      <strong>No elegibles (no se enviarán):</strong>
                      <ul className="mt-1 list-disc list-inside">
                        {noElegibles.map((f) => (
                          <li key={f.envio_id}>
                            {f.referenciaexterna} — {f.motivo_no_elegible}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {elegibles.length > 0 && (
                <div className="mt-6 overflow-x-auto">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-medium text-gray-900">
                      Facturas elegibles ({elegibles.length})
                    </h3>
                    <label className="flex items-center text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={
                          elegibles.length > 0 &&
                          selectedIds.size === elegibles.length
                        }
                        onChange={toggleSelectAll}
                        className="mr-2 rounded border-gray-300 text-primary-600"
                      />
                      Seleccionar todos
                    </label>
                  </div>
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left w-10" />
                        <th className="px-3 py-2 text-left">Referencia ext.</th>
                        <th className="px-3 py-2 text-left">Consecutivo</th>
                        <th className="px-3 py-2 text-left">Referencia NDC</th>
                        <th className="px-3 py-2 text-left">Receptor</th>
                        <th className="px-3 py-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {elegibles.map((f) => (
                        <tr key={f.envio_id} className="hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(f.envio_id)}
                              onChange={() => toggleSelect(f.envio_id)}
                              className="rounded border-gray-300 text-primary-600"
                            />
                          </td>
                          <td className="px-3 py-2">{f.referenciaexterna}</td>
                          <td className="px-3 py-2">{f.consecutivo || '—'}</td>
                          <td className="px-3 py-2 font-mono text-xs">
                            {f.referencia_int}
                          </td>
                          <td className="px-3 py-2">{f.nombrereceptor || '—'}</td>
                          <td className="px-3 py-2 text-right">
                            {formatCurrency(f.totalfactura)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Confirmar anulación ({selectedFacturas.length} documentos)
                </h3>
                <div className="space-x-2">
                  <button
                    onClick={handleVolver}
                    className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                  >
                    Volver
                  </button>
                </div>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notas (aplican a todas las notas de crédito)
              </label>
              <textarea
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                rows={3}
                placeholder="Motivo de la nota de crédito..."
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500 mb-4"
              />

              <div className="overflow-x-auto mb-4">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Referencia ext.</th>
                      <th className="px-3 py-2 text-left">Clave numérica</th>
                      <th className="px-3 py-2 text-left">Referencia NDC</th>
                      <th className="px-3 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {selectedFacturas.map((f) => (
                      <tr key={f.envio_id}>
                        <td className="px-3 py-2">{f.referenciaexterna}</td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {f.clave_numerica}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {f.referencia_int}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {formatCurrency(f.totalfactura)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                onClick={() => {
                  if (!notas.trim()) {
                    setError('Las notas son obligatorias');
                    return;
                  }
                  setError('');
                  emitStartedRef.current = false;
                  setEmitFinished(false);
                  setStep(3);
                }}
                disabled={selectedFacturas.length === 0}
                className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                Confirmar y emitir
              </button>
            </div>
          )}

          {step === 3 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Procesando notas de crédito
              </h3>

              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>
                    {processing
                      ? `Procesando... ${processed} de ${totalToProcess}`
                      : `Completado: ${processed} de ${totalToProcess}`}
                  </span>
                  <span>{progressPercent}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-primary-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>

              {emitFinished && (
                <div
                  className={`mb-4 p-3 rounded border text-sm ${
                    errorCount === 0
                      ? 'bg-green-50 border-green-200 text-green-800'
                      : 'bg-yellow-50 border-yellow-200 text-yellow-800'
                  }`}
                >
                  Finalizado: {okCount} exitosas, {errorCount} con error
                </div>
              )}

              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Referencia ext.</th>
                      <th className="px-3 py-2 text-left">Referencia NDC</th>
                      <th className="px-3 py-2 text-left">Estado</th>
                      <th className="px-3 py-2 text-left">Mensaje</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {resultItems.map((r) => (
                      <tr key={r.envio_id}>
                        <td className="px-3 py-2">{r.referenciaexterna}</td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {r.referencia_int}
                        </td>
                        <td className="px-3 py-2">
                          {r.status === 'pendiente' && (
                            <span className="text-gray-500">Pendiente</span>
                          )}
                          {r.status === 'ok' && (
                            <span className="text-green-600 font-medium">OK</span>
                          )}
                          {r.status === 'error' && (
                            <span className="text-red-600 font-medium">Error</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">{r.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {emitFinished && (
                <button
                  onClick={handleReiniciar}
                  className="mt-4 px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700"
                >
                  Nueva emisión
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotaCreditoWizard;
